import React, { FC, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import VerificationInput from "react-verification-input";

import { Box, Stack, Typography } from "@mui/material";

import { Button as GradientButton } from "@ui/index";

import { InputWrapper } from "@components/SignUpDialog/components/VerifyEmail/components";

import { CheckEmailIcon } from "@/assets/svg";
import { DescriptionTypography } from "@/components/LoginDialog/components";
import { MainTextTypography } from "@/components/RecoveryDialog/components";
import { useIsDesktop } from "@/hooks/layout";
import { logout, sendVerificationCode, verifyCode } from "@/store/actions/user";
import { useAppDispatchUnwrap, useAppSelector } from "@/store/hooks";
import { ERoutes, NUMBER_OF_CODE_DIGITS } from "@/utils/constants";

type TVerifyEmail = {
  handleNextStep: () => void;
  /** Dev preview (DevModalLauncher): skip the on-mount code send + countdown
      and seed a sample email so the step can be opened directly for layout
      testing without a real signup session. */
  devPreview?: boolean;
};

const VerifyEmail: FC<TVerifyEmail> = ({ handleNextStep, devPreview }) => {
  const { t } = useTranslation("signup", {
    keyPrefix: "verifyEmail",
  });
  const isDesktop = useIsDesktop();
  const authRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<NodeJS.Timer>(null);
  const codeRef = useRef<HTMLButtonElement>(null);
  const countRef = useRef(30);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const [errorText, setErrorText] = useState<string>("");
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const dispatch = useAppDispatchUnwrap();
  const email = useAppSelector((state) => (state.user as any)?.user?.email) || (devPreview ? "creator@example.com" : "");

  const sendCode = async () => {
    await dispatch(sendVerificationCode());
  };

  // Escape hatch: the sign-up dialog is non-closable, so an unverified user
  // who has no code (or doesn't want to verify right now) would otherwise be
  // stuck. Log out + hard-navigate home to drop the unverified session and
  // unblock the site (mirrors the AccountRestoreModal logout pattern).
  const handleLogout = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("tokens") || "{}");
      await dispatch(logout({ accessToken: tokens?.accessToken || "", refreshToken: tokens?.refreshToken || "" }));
    } catch { /* non-fatal */ }
    window.location.href = ERoutes.Home;
  };

  const startCoundown = () => {
    countRef.current = 30;
    codeRef.current.textContent = t("resendButtonCounter");
    setIsButtonEnabled(false);
    timerRef.current = setInterval(() => {
      const count = countRef.current;

      if (!count) {
        codeRef.current.textContent = t("resendButton");

        clearInterval(timerRef.current);
        setIsButtonEnabled(true);
        return;
      }
      countRef.current = count - 1;
      codeRef.current.textContent = `New code available in ${count}s`;
    }, 1000);
  };

  useEffect(() => {
    if (devPreview) return; // dev preview: no real code send / countdown
    sendCode();
    startCoundown();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (authRef.current) {
        authRef.current.focus();
      }
    }, 400);
  }, []);

  // Auto-verify as soon as the code reaches full length — handles both
  // typing the last digit and pasting all six. Blurs the input immediately
  // so the user sees the transition instead of a focused "still waiting"
  // box. Clears on both success and error so the effect can't re-fire
  // against a stale (already-consumed or wrong) code — the SignUp flow
  // keeps this component mounted during the slide transition, which was
  // letting the effect retrigger and hit the backend with an exhausted
  // verification code.
  useEffect(() => {
    if (code.length !== NUMBER_OF_CODE_DIGITS || isVerifying) return;
    if (devPreview) { handleNextStep(); return; } // preview: just advance, no backend
    setIsVerifying(true);
    setErrorText("");
    authRef.current?.blur();
    (async () => {
      try {
        await dispatch(verifyCode({ code: Number(code) }));
        setCode("");
        handleNextStep();
      } catch (res: any) {
        setErrorText(res?.message ?? "Invalid code");
        setCode("");
      } finally {
        setIsVerifying(false);
      }
    })();
  }, [code, isVerifying, dispatch, handleNextStep]);

  return (
    <Stack spacing={isDesktop ? "32px" : "24px"} width="100%" justifyContent="center" alignItems="center">
      <Stack spacing={isDesktop ? "32px" : "16px"} width="100%" justifyContent="center" alignItems="center">
        <Stack spacing="16px" height="100%" width="100%" justifyContent="center" alignItems="center">
          <CheckEmailIcon />
          <Stack spacing="16px" justifyContent="center" alignItems="center">
            <MainTextTypography textAlign="center">{t("mainText")}</MainTextTypography>

            <DescriptionTypography>
              <Trans
                t={t}
                i18nKey="description"
                values={{ email }}
                components={{ bold: <strong style={{ color: "#fbf4ec" }} /> }}
              />
            </DescriptionTypography>
          </Stack>
        </Stack>
        <InputWrapper>
          <VerificationInput
            ref={authRef}
            classNames={{
              container: "container",
              character: "character",
              characterSelected: "character-selected",
            }}
            value={code}
            onChange={setCode}
            placeholder=""
            validChars="0-9"
            inputProps={{
              autoComplete: "one-time-code",
              inputMode: "numeric",
            }}
          />
          {Boolean(errorText.length) && (
            <Typography textAlign="center" sx={{ color: "#e47a6a", fontFamily: "'Raleway', sans-serif", fontSize: "0.85rem" }} mt="8px">
              {errorText}
            </Typography>
          )}
        </InputWrapper>
        <Stack width="100%" mt="8px" spacing="12px" alignItems="center">
          <GradientButton
            isBigButton
            isFullWidth
            type="submit"
            isDisabled={!isButtonEnabled}
            ref={codeRef}
            onClick={() => sendCode() && startCoundown()}
          >
            {t("resendButton")}
          </GradientButton>
          {/* Plain text, not a button. Logging out is the way OUT of this step,
              not a second thing to do in it — as a full-width bordered button it
              sat level with "Resend code" and read as an equal choice. Quiet
              white text keeps it findable for someone genuinely stuck without
              competing with the action the modal exists for. */}
          <Box
            component="button"
            type="button"
            onClick={handleLogout}
            sx={{
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              fontFamily: "'Raleway', sans-serif",
              fontSize: "0.8rem",
              fontWeight: 400,
              opacity: 0.7,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1, textDecoration: "underline" },
            }}
          >
            {t("logout")}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default VerifyEmail;
